import { useEffect, useMemo, useState, useRef } from "react";
import { useSearchParams, useParams, useNavigate, useLocation } from "react-router-dom";
import AppShell from "../components/AppShell";
import { api, API } from "../lib/api";
import { logError } from "../lib/log";
import { useAuth } from "../context/AuthContext";
import {
  Sparkles, Loader2,
  Building, Bed, Wand2, Users, MapPin,
  Plus, Pencil, Trash2, Check,
} from "lucide-react";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

import {
  TYPES, VINTAGES, MODELS, DORM_SIZES, BD_MEMBERS,
  newMemberRow, avgKey, numProps,
  fmtL, fmtPct, fmtNum,
} from "./property-ic/utils";
import { Section, Field, Stat } from "./property-ic/_primitives";
import PipelinePanel from "./property-ic/PipelinePanel";
import SourceEmailPanel from "./property-ic/SourceEmailPanel";
import SubmissionsHistory from "./property-ic/SubmissionsHistory";
import MarketCompetitorIntel from "./property-ic/MarketCompetitorIntel";
import MisIntelligence from "./property-ic/MisIntelligence";
import ICDocument from "./property-ic/ICDocument";
import ICEditBanner from "./property-ic/ICEditBanner";
import { KpiStrip, RevenueBand, OpexTable, FinancialTotalsStrip, DecisionMetricsStrip, ScenarioToggle } from "./property-ic/ICPnlPanel";
import ICSubmitBar from "./property-ic/ICSubmitBar";

// ============ Main page ============
// `mode` is set by the route: "drafts" renders the pipeline + past submissions
// panels; "submission" renders the full IC form. All data-loading, calculations,
// Gmail sync, and submit logic below are shared across both — the two pages just
// hide/show different chunks of the render tree.
export default function PropertyIC({ mode = "drafts" }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const routeParams = useParams();
  const routeLocation = useLocation();
  const routeSubmissionId = routeParams.id && routeParams.id !== "new" ? routeParams.id : null;
  const canApprove = !!(user?.is_admin || user?.permissions?.property_ic_approver || user?.can_approve_ic);

  const [pipe, setPipe] = useState({ items: [], actioned: [], loading: false, error: null, sync: null });
  const [subs, setSubs] = useState([]);
  const [misProps, setMisProps] = useState([]);
  // editing: null OR { id, decision, created_at, submitted_by_name, locked }
  const [editing, setEditing] = useState(null);

  const [meta, setMeta] = useState({ property_name: "", city: "", state: "", partner_name: "", notes: "", operation_start_date: "", property_link: "", source_email_id: null, source_email_subject: null, source_email_from: null, source_email_date: null, source_email_body: null });
  const [params, setParams] = useState({ type: "Leisure - North", vintage: "New", model: "COCO", fnb_per_bed_per_day: 50 });
  const EMPTY_ROOMS = { dorm_4: 0, dorm_6: 0, dorm_8: 0, dorm_10: 0, dorm_12: 0, dorm_16: 0, private: 0, blocked: 0 };
  const DEFAULT_ROOMS = { dorm_4: 0, dorm_6: 0, dorm_8: 4, dorm_10: 4, dorm_12: 0, dorm_16: 0, private: 4, blocked: 0 };
  const [roomOptions, setRoomOptions] = useState([{ ...DEFAULT_ROOMS }, { ...EMPTY_ROOMS }, { ...EMPTY_ROOMS }]);
  const [activeOption, setActiveOption] = useState(0); // 0/1/2
  const rooms = roomOptions[activeOption];
  const setRooms = (updater) => {
    setRoomOptions(prev => prev.map((opt, i) => {
      if (i !== activeOption) return opt;
      return typeof updater === "function" ? updater(opt) : updater;
    }));
  };
  const [memberEstimates, setMemberEstimates] = useState(BD_MEMBERS.map(newMemberRow));
  // BD-members roster edit state — admin-only. Non-admins see the Estimates
  // table exactly as before; value inputs stay editable for everyone, only the
  // members list itself is admin-managed.
  const isAdmin = !!user?.is_admin;
  const [editMembersMode, setEditMembersMode] = useState(false);
  const [renamingIdx, setRenamingIdx] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [renameError, setRenameError] = useState(null); // { idx, msg }
  const [addingMember, setAddingMember] = useState(false);
  const [newMemberName, setNewMemberName] = useState("");
  const [addError, setAddError] = useState(null);
  const [opOverrides, setOpOverrides] = useState({}); // {line_item: monthly_amount} per-property edits
  const [aiSugg, setAiSugg] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [misRates, setMisRates] = useState({ rates: {}, sample_size: 0, latest_month: null });
  const [breakeven, setBreakeven] = useState(null);
  const [shooting, setShooting] = useState(false);
  const formRef = useRef(null);
  const docRef = useRef(null); // off-screen one-pager IC doc captured by Screenshot

  const [calc, setCalc] = useState(null);
  const [calcLoading, setCalcLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [activeScenario, setActiveScenario] = useState("base");

  const estDorm = useMemo(() => ({
    occ_pct: avgKey(memberEstimates, "dorm_occ_pct"),
    adr: avgKey(memberEstimates, "dorm_adr"),
  }), [memberEstimates]);
  const estPriv = useMemo(() => ({
    occ_pct: avgKey(memberEstimates, "private_occ_pct"),
    adr: avgKey(memberEstimates, "private_adr"),
  }), [memberEstimates]);

  const loadPipeline = async () => {
    setPipe(s => ({ ...s, loading: true, error: null }));
    try {
      // Cache-first: backend pulls cached items + does incremental fetch of last few days.
      const { data } = await api.get("/property-ic/inbox");
      setPipe({
        items: data.items || [],
        actioned: data.actioned || [],
        loading: false,
        error: data?.sync?.error || null,
        sync: data?.sync || null,
      });
    } catch (e) {
      setPipe(s => ({ ...s, items: s.items, actioned: s.actioned, loading: false, error: e?.response?.data?.detail || e.message }));
    }
  };
  const loadSubs = async () => { try { const { data } = await api.get("/property-ic/submissions"); setSubs(data || []); } catch (e) { logError("loadSubs failed", e); } };
  const loadMisProps = async () => { try { const { data } = await api.get("/property-ic/mis-properties"); setMisProps(data.properties || []); } catch (e) { logError("loadMisProps failed", e); } };
  const loadMisRates = async (categoryType) => {
    try {
      const { data } = await api.get(`/property-ic/mis-current-rates?type=${encodeURIComponent(categoryType)}`);
      const c = data?.category || {};
      setMisRates({ rates: c.rates || {}, sample_size: c.sample_size || 0, latest_month: data?.latest_month || null });
    } catch (e) { logError("loadMisRates failed", e); }
  };

  useEffect(() => { loadPipeline(); loadSubs(); loadMisProps(); }, []);
  // Refresh MIS benchmark whenever the IC type changes
  useEffect(() => { loadMisRates(params.type); }, [params.type]);

  // Auto-open a submission when navigated via topbar search (?open=<submission_id>)
  const [searchParams, setSearchParams] = useSearchParams();
  const openId = searchParams.get("open");
  useEffect(() => {
    if (!openId || !subs.length) return;
    const target = subs.find((s) => s.submission_id === openId);
    if (target) {
      onOpenSub(target);
      // Strip the param so re-renders don't re-trigger
      const next = new URLSearchParams(searchParams);
      next.delete("open");
      setSearchParams(next, { replace: true });
    }
    // onOpenSub + setSearchParams are render-stable; searchParams is reset to a
    // non-`open` value inside this branch, so further deps would just churn.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openId, subs]);

  // Auto-load a submission when the route is /property-ic/submission/:id.
  // Runs once subs are fetched and whenever the URL id changes.
  useEffect(() => {
    if (mode !== "submission" || !routeSubmissionId || !subs.length) return;
    if (editing?.id === routeSubmissionId) return; // already loaded
    const target = subs.find((s) => s.submission_id === routeSubmissionId);
    if (target) {
      const inp = target.inputs || {};
      setMeta({
        property_name: target.property_name || "",
        city: target.city || "",
        state: target.state || "",
        partner_name: target.partner_name || "",
        notes: target.notes || "",
        operation_start_date: target.operation_start_date || "",
        property_link: target.property_link || "",
        source_email_id: target.source_email_id || null,
        source_email_subject: target.source_email_subject || null,
        source_email_from: target.source_email_from || null,
        source_email_date: target.source_email_date || null,
        source_email_body: target.source_email_body || null,
      });
      setParams({
        type: inp.type || "Leisure - North",
        vintage: inp.vintage || "New",
        model: inp.model || "COCO",
        fnb_per_bed_per_day: Math.round((inp.fnb_per_bed_per_day ?? 50) * 10) / 10,
      });
      const fallback = (b) => ({ ...EMPTY_ROOMS, ...(b || {}) });
      if (Array.isArray(inp.room_options) && inp.room_options.length) {
        const opts = [0, 1, 2].map(i => fallback(inp.room_options[i]));
        setRoomOptions(opts);
        const idx = Number.isInteger(inp.active_option) ? Math.max(0, Math.min(2, inp.active_option)) : 0;
        setActiveOption(idx);
      } else {
        setRoomOptions([fallback(inp.room_breakdown), { ...EMPTY_ROOMS }, { ...EMPTY_ROOMS }]);
        setActiveOption(0);
      }
      const round1 = (v) => Math.round((v ?? 0) * 10) / 10;
      // Members roster from persisted `inputs.members` — backend backfills
      // this to the hardcoded 6 defaults on read for legacy docs.
      const memberNames = (Array.isArray(inp.members) && inp.members.length) ? inp.members : BD_MEMBERS;
      if (Array.isArray(inp.member_estimates) && inp.member_estimates.length) {
        const byMember = Object.fromEntries(inp.member_estimates.map(m => [m.member, m]));
        setMemberEstimates(memberNames.map(m => {
          const r = byMember[m] || {};
          return { member: m, dorm_occ_pct: round1(r.dorm_occ_pct), dorm_adr: Math.round(r.dorm_adr || 0), private_occ_pct: round1(r.private_occ_pct), private_adr: Math.round(r.private_adr || 0) };
        }));
      } else {
        const dOcc = round1(inp.dorm_occ_pct), dAdr = Math.round(inp.dorm_adr || 0);
        const pOcc = round1(inp.private_occ_pct), pAdr = Math.round(inp.private_adr || 0);
        setMemberEstimates(memberNames.map(m => ({ member: m, dorm_occ_pct: dOcc, dorm_adr: dAdr, private_occ_pct: pOcc, private_adr: pAdr })));
      }
      setOpOverrides(inp.operational_overrides || {});
      setEditing({
        id: target.submission_id,
        decision: target.decision,
        created_at: target.created_at,
        submitted_by_name: target.submitted_by_name || target.submitted_by,
        locked: target.decision === "Approved" && !user?.is_admin,
        editing_approved: target.decision === "Approved" && !!user?.is_admin,
        legacy_estimates: !Array.isArray(inp.member_estimates) || !inp.member_estimates.length,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, routeSubmissionId, subs]);

  // When the user arrives at /submission/new from a pipeline "Open IC" click,
  // the pipeline item is passed via router state. Apply it once, then clear.
  useEffect(() => {
    if (mode !== "submission") return;
    const prefill = routeLocation.state?.emailPrefill;
    if (prefill) {
      applyEmailPrefill(prefill);
      // Clear state so it doesn't re-apply on re-render / hot reload
      window.history.replaceState({}, "", routeLocation.pathname);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  // Fresh-draft BD members seed: on entering /property-ic/submission/new, copy
  // the roster from the most recently modified IC (backend endpoint returns the
  // hardcoded 6 defaults if no IC exists yet). Only fires when NO submissionId
  // is in the URL — the hydration effect above owns the existing-submission path.
  useEffect(() => {
    if (mode !== "submission" || routeSubmissionId) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/property-ic/members-seed");
        if (cancelled) return;
        const list = Array.isArray(data?.members) && data.members.length ? data.members : BD_MEMBERS;
        setMemberEstimates(list.map(newMemberRow));
      } catch (e) { logError("members-seed failed", e); }
    })();
    return () => { cancelled = true; };
  }, [mode, routeSubmissionId]);

  // Live calculate
  useEffect(() => {
    const t = setTimeout(async () => {
      setCalcLoading(true);
      try {
        const { data } = await api.post("/property-ic/calculate", {
          room_breakdown: { dorm_4: +rooms.dorm_4 || 0, dorm_6: +rooms.dorm_6 || 0, dorm_8: +rooms.dorm_8 || 0, dorm_10: +rooms.dorm_10 || 0, dorm_12: +rooms.dorm_12 || 0, dorm_16: +rooms.dorm_16 || 0, private: +rooms.private || 0, blocked: +rooms.blocked || 0 },
          dorm_adr: estDorm.adr || 0, dorm_occ_pct: estDorm.occ_pct || 0,
          private_adr: estPriv.adr || 0, private_occ_pct: estPriv.occ_pct || 0,
          fnb_per_bed_per_day: +params.fnb_per_bed_per_day || 0,
          type: params.type, vintage: params.vintage, model: params.model,
          operational_overrides: opOverrides,
        });
        setCalc(data);
      } catch (e) { logError("ic-calculate failed", e); } finally { setCalcLoading(false); }
    }, 350);
    return () => clearTimeout(t);
  }, [rooms, estDorm, estPriv, params, opOverrides]);

  // Break-even rent suggestion (refreshes on calc change). Solves for the
  // monthly Rent that yields a 36-month payback.
  useEffect(() => {
    const t = setTimeout(async () => {
      try {
        const { data } = await api.post("/property-ic/suggest-rent", {
          room_breakdown: { dorm_4: +rooms.dorm_4 || 0, dorm_6: +rooms.dorm_6 || 0, dorm_8: +rooms.dorm_8 || 0, dorm_10: +rooms.dorm_10 || 0, dorm_12: +rooms.dorm_12 || 0, dorm_16: +rooms.dorm_16 || 0, private: +rooms.private || 0, blocked: +rooms.blocked || 0 },
          dorm_adr: estDorm.adr || 0, dorm_occ_pct: estDorm.occ_pct || 0,
          private_adr: estPriv.adr || 0, private_occ_pct: estPriv.occ_pct || 0,
          fnb_per_bed_per_day: +params.fnb_per_bed_per_day || 0,
          type: params.type, vintage: params.vintage, model: params.model,
          operational_overrides: opOverrides,
        });
        setBreakeven(data);
      } catch (e) { logError("breakeven-rent failed", e); }
    }, 600);
    return () => clearTimeout(t);
  }, [rooms, estDorm, estPriv, params, opOverrides]);

  // ---- Screenshot helper: capture the form area + KPI cards as a PNG ----
  const handleScreenshot = async () => {
    if (!docRef.current) return;
    setShooting(true);
    try {
      // Wait for webfonts to finish loading — prevents html2canvas from
      // mis-measuring glyph heights (which clips bold display numbers).
      if (document.fonts && document.fonts.ready) {
        try { await document.fonts.ready; } catch (e) { logError("fonts.ready rejected", e); }
      }
      const html2canvas = (await import("html2canvas")).default;
      const canvas = await html2canvas(docRef.current, {
        backgroundColor: "#ffffff",
        scale: 2,
        useCORS: true,
        windowWidth: 820,
        width: 820,
        // Capture full document height even if it overflows the off-screen viewport
        height: docRef.current.scrollHeight,
        windowHeight: docRef.current.scrollHeight,
      });
      const link = document.createElement("a");
      link.download = `IC-${(meta.property_name || "draft").replace(/\s+/g, "_")}-${new Date().toISOString().slice(0, 10)}.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
      toast.success("IC document saved · ready to share with BD team");
    } catch (e) { logError("screenshot failed", e); toast.error("Could not capture screenshot"); }
    finally { setShooting(false); }
  };

  const applyEmailPrefill = (it) => {
    setMeta(m => ({
      ...m,
      property_name: it.property_guess || it.subject,
      partner_name: (it.from || "").split("<")[0].trim(),
      property_link: it.property_link || "",
      source_email_id: it.id,
      source_email_subject: it.subject,
      source_email_from: it.from,
      source_email_date: it.date,
      source_email_body: it.body || it.snippet,
    }));
    toast.success(`Pre-filled from email${it.property_link ? " (link captured)" : ""}`);
    document.querySelector("[data-testid='ic-property-name']")?.focus();
  };
  const onUseEmail = (it) => {
    // On the drafts page, "Open IC" hops to the submission page with the email
    // prefill carried via router state. On the submission page it just applies.
    if (mode === "drafts") {
      navigate("/property-ic/submission/new", { state: { emailPrefill: it } });
    } else {
      applyEmailPrefill(it);
    }
  };
  const onDismiss = async (it) => {
    try { await api.post("/property-ic/inbox/dismiss", { email_id: it.id });
      setPipe(s => ({ ...s, items: s.items.filter(x => x.id !== it.id), actioned: [{ ...it, dismissed_decision: "Dismissed", dismissed_by: user.email }, ...s.actioned] }));
    } catch { toast.error("Failed"); }
  };
  const onQuickDecide = async (it, decision) => {
    try {
      await api.post("/property-ic/inbox/quick-decision", { email_id: it.id, subject: it.subject || "", sender: it.from || "", property_guess: it.property_guess || "", decision });
      toast.success(`Marked ${decision}`);
      setPipe(s => ({ ...s, items: s.items.filter(x => x.id !== it.id), actioned: [{ ...it, dismissed_decision: decision, dismissed_by: user.email }, ...s.actioned] }));
      loadSubs();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const onAiSuggest = async () => {
    if (!meta.city) { toast.error("City required"); return; }
    setAiLoading(true);
    try {
      const totalBeds = calc?.room_summary?.total_beds || 0;
      const { data } = await api.post("/property-ic/ai-suggest", { city: meta.city, state: meta.state, type: params.type, total_beds: totalBeds });
      setAiSugg(data.suggestion || null);
      if (data.suggestion?.dorm && data.suggestion?.private) {
        setMemberEstimates(arr => arr.map(r => ({ ...r, dorm_occ_pct: data.suggestion.dorm.occ_pct, dorm_adr: data.suggestion.dorm.adr, private_occ_pct: data.suggestion.private.occ_pct, private_adr: data.suggestion.private.adr * 2 /* per bed → per room */ })));
      }
      toast.success(data.fallback ? "Filled from heuristic" : "AI estimates applied to all members");
    } catch { toast.error("AI suggest failed"); }
    finally { setAiLoading(false); }
  };
  const onApplyMis = (p) => {
    if (!p) return;
    const occ = Math.round(p.dorm_occ_pct || p.avg_occupancy_pct || 60);
    const adr = Math.round(p.dorm_adr || p.avg_adr || 900);
    const pOcc = Math.round(p.private_occ_pct || p.avg_occupancy_pct * 0.9 || 50);
    const pAdr = Math.round(p.private_adr || p.private_adr_room || (p.avg_adr * 1.6) || 2200);
    setMemberEstimates(arr => arr.map(r => ({ ...r, dorm_occ_pct: occ, dorm_adr: adr, private_occ_pct: pOcc, private_adr: pAdr })));
    toast.success(`Applied ${p.location} averages to all members`);
  };
  const resetForm = async () => {
    setEditing(null);
    setMeta({ property_name: "", city: "", state: "", partner_name: "", notes: "", operation_start_date: "", property_link: "", source_email_id: null, source_email_subject: null, source_email_from: null, source_email_date: null, source_email_body: null });
    setParams({ type: "Leisure - North", vintage: "New", model: "COCO", fnb_per_bed_per_day: 50 });
    setRoomOptions([{ ...DEFAULT_ROOMS }, { ...EMPTY_ROOMS }, { ...EMPTY_ROOMS }]);
    setActiveOption(0);
    // Fresh draft — seed BD roster from the most recently modified IC (falls
    // back to the hardcoded 6 defaults if the backend call fails).
    try {
      const { data } = await api.get("/property-ic/members-seed");
      const list = Array.isArray(data?.members) && data.members.length ? data.members : BD_MEMBERS;
      setMemberEstimates(list.map(newMemberRow));
    } catch (e) {
      logError("members-seed failed in resetForm", e);
      setMemberEstimates(BD_MEMBERS.map(newMemberRow));
    }
    setOpOverrides({});
    setAiSugg(null);
    setEditMembersMode(false);
    setRenamingIdx(null);
    setRenameError(null);
    setAddingMember(false);
    setNewMemberName("");
    setAddError(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // ---- BD members list handlers (admin-only) ----
  const commitRename = (idx) => {
    const nm = renameValue.trim();
    if (!nm) { setRenamingIdx(null); setRenameError(null); return; }
    if (memberEstimates[idx] && nm === memberEstimates[idx].member) { setRenamingIdx(null); setRenameError(null); return; }
    const dup = memberEstimates.some((x, i) => i !== idx && x.member.toLowerCase() === nm.toLowerCase());
    if (dup) { setRenameError({ idx, msg: "Name already exists" }); return; }
    setMemberEstimates(arr => arr.map((x, i) => i === idx ? { ...x, member: nm } : x));
    setRenamingIdx(null);
    setRenameError(null);
  };
  const deleteMember = (idx) => {
    setMemberEstimates(arr => arr.filter((_, i) => i !== idx));
    if (renamingIdx === idx) { setRenamingIdx(null); setRenameError(null); }
  };
  const commitAdd = () => {
    const nm = newMemberName.trim();
    if (!nm) { setAddingMember(false); setNewMemberName(""); setAddError(null); return; }
    const dup = memberEstimates.some(x => x.member.toLowerCase() === nm.toLowerCase());
    if (dup) { setAddError("Name already exists"); return; }
    setMemberEstimates(arr => [...arr, { member: nm, dorm_occ_pct: 0, dorm_adr: 0, private_occ_pct: 0, private_adr: 0 }]);
    setNewMemberName("");
    setAddingMember(false);
    setAddError(null);
  };
  const cancelAdd = () => { setAddingMember(false); setNewMemberName(""); setAddError(null); };

  const onOpenSub = (s) => {
    // On the drafts page, opening a saved submission jumps to its submission
    // URL and lets the submission page load it (same code path used when the
    // user lands there directly). On the submission page we load in-place.
    if (mode === "drafts") {
      navigate(`/property-ic/submission/${s.submission_id}`);
      return;
    }
    const inp = s.inputs || {};
    setMeta({
      property_name: s.property_name || "",
      city: s.city || "",
      state: s.state || "",
      partner_name: s.partner_name || "",
      notes: s.notes || "",
      operation_start_date: s.operation_start_date || "",
      property_link: s.property_link || "",
      source_email_id: s.source_email_id || null,
      source_email_subject: s.source_email_subject || null,
      source_email_from: s.source_email_from || null,
      source_email_date: s.source_email_date || null,
      source_email_body: s.source_email_body || null,
    });
    setParams({
      type: inp.type || "Leisure - North",
      vintage: inp.vintage || "New",
      model: inp.model || "COCO",
      fnb_per_bed_per_day: Math.round((inp.fnb_per_bed_per_day ?? 50) * 10) / 10,
    });
    // Three independent room-breakdown options. Backwards-compatible:
    // if older submission only has `room_breakdown`, use it as Option 1.
    const fallback = (b) => ({ ...EMPTY_ROOMS, ...(b || {}) });
    if (Array.isArray(inp.room_options) && inp.room_options.length) {
      const opts = [0, 1, 2].map(i => fallback(inp.room_options[i]));
      setRoomOptions(opts);
      const idx = Number.isInteger(inp.active_option) ? Math.max(0, Math.min(2, inp.active_option)) : 0;
      setActiveOption(idx);
    } else {
      setRoomOptions([fallback(inp.room_breakdown), { ...EMPTY_ROOMS }, { ...EMPTY_ROOMS }]);
      setActiveOption(0);
    }
    const round1 = (v) => Math.round((v ?? 0) * 10) / 10;
    // Per-draft members roster (backend synthesizes to the 6 defaults on read
    // when the field is absent). Uses this list — not the hardcoded BD_MEMBERS —
    // so admin-renamed or admin-added members are hydrated correctly.
    const memberNames = (Array.isArray(inp.members) && inp.members.length) ? inp.members : BD_MEMBERS;
    if (Array.isArray(inp.member_estimates) && inp.member_estimates.length) {
      const byMember = Object.fromEntries(inp.member_estimates.map(m => [m.member, m]));
      setMemberEstimates(memberNames.map(m => {
        const r = byMember[m] || {};
        return {
          member: m,
          dorm_occ_pct: round1(r.dorm_occ_pct),
          dorm_adr: Math.round(r.dorm_adr || 0),
          private_occ_pct: round1(r.private_occ_pct),
          private_adr: Math.round(r.private_adr || 0),
        };
      }));
    } else {
      // Older submissions saved only the averages — spread across all rows so
      // the deal still re-computes identically. We round so the inputs read
      // cleanly (e.g. 70.8 instead of 70.83333…).
      const dOcc = round1(inp.dorm_occ_pct), dAdr = Math.round(inp.dorm_adr || 0);
      const pOcc = round1(inp.private_occ_pct), pAdr = Math.round(inp.private_adr || 0);
      setMemberEstimates(memberNames.map(m => ({ member: m, dorm_occ_pct: dOcc, dorm_adr: dAdr, private_occ_pct: pOcc, private_adr: pAdr })));
    }
    setOpOverrides(inp.operational_overrides || {});
    setEditing({
      id: s.submission_id,
      decision: s.decision,
      created_at: s.created_at,
      submitted_by_name: s.submitted_by_name || s.submitted_by,
      locked: s.decision === "Approved" && !user?.is_admin,
      editing_approved: s.decision === "Approved" && !!user?.is_admin,
      legacy_estimates: !Array.isArray(inp.member_estimates) || !inp.member_estimates.length,
    });
    setAiSugg(null);
    const label = s.decision === "Approved"
      ? (user?.is_admin ? " · editing Approved (admin)" : " · locked")
      : " for editing";
    toast.success(`Opened ${s.property_name}${label}`);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const submit = async (decision) => {
    if (!meta.property_name.trim()) { toast.error("Property name required"); return; }
    setSubmitting(true);
    try {
      const body = {
        ...meta, decision,
        inputs: {
          room_breakdown: rooms, member_estimates: memberEstimates,
          // Ordered BD-members roster for this draft (admin-editable). Backend
          // persists it verbatim; on-read backfill uses the hardcoded default.
          members: memberEstimates.map(m => m.member),
          room_options: roomOptions, active_option: activeOption,
          dorm_adr: estDorm.adr || 0, dorm_occ_pct: estDorm.occ_pct || 0,
          private_adr: estPriv.adr || 0, private_occ_pct: estPriv.occ_pct || 0,
          fnb_per_bed_per_day: +params.fnb_per_bed_per_day || 0,
          type: params.type, vintage: params.vintage, model: params.model,
          operational_overrides: opOverrides,
        },
      };
      if (editing?.id) {
        const { data } = await api.put(`/property-ic/submissions/${editing.id}`, body);
        toast.success(decision === "Draft" ? "Draft saved" : `Updated · ${decision}`);
        // Stay on the same submission, refresh local editing state
        setEditing({ id: data.submission.submission_id, decision: data.submission.decision, created_at: data.submission.created_at, submitted_by_name: data.submission.submitted_by_name, locked: data.submission.decision === "Approved" && !user?.is_admin, editing_approved: data.submission.decision === "Approved" && !!user?.is_admin });
        loadSubs();
      } else {
        const { data } = await api.post("/property-ic/submit", body);
        if (decision === "Draft") {
          // Keep form open — user can continue editing this draft
          toast.success("Draft saved");
          setEditing({ id: data.submission.submission_id, decision: "Draft", created_at: data.submission.created_at, submitted_by_name: data.submission.submitted_by_name, locked: false });
          loadSubs();
          // Move the URL onto the newly-created record so the user stays anchored to it
          if (!routeSubmissionId) navigate(`/property-ic/submission/${data.submission.submission_id}`, { replace: true });
        } else {
          toast.success(`Saved as ${decision}`);
          window.open(`${API}/property-ic/submissions/${data.submission.submission_id}/export`, "_blank");
          loadSubs(); loadPipeline();
          // Anchor URL to the freshly-created submission before resetting local state
          if (!routeSubmissionId) navigate(`/property-ic/submission/${data.submission.submission_id}`, { replace: true });
          resetForm();
        }
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Submit failed"); }
    finally { setSubmitting(false); }
  };
  const onDeleteSub = async (s) => {
    if (!window.confirm(`Delete IC submission for ${s.property_name}?`)) return;
    try {
      await api.delete(`/property-ic/submissions/${s.submission_id}`);
      toast.success("Deleted");
      if (editing?.id === s.submission_id) resetForm();
      loadSubs();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  const sc = calc && (activeScenario === "bull" ? calc.scenario_bull : activeScenario === "bear" ? calc.scenario_bear : calc.base_case);
  const rs = calc?.room_summary || { total_rooms: 0, total_beds: 0, dorm_beds: 0, private_beds: 0, dorm_rooms: 0, private_rooms: 0 };
  const dormBedRoomRatio = rs.dorm_rooms > 0 ? (rs.dorm_beds / rs.dorm_rooms).toFixed(1) : "—";

  const inputClass = "h-9 rounded-md border-zinc-200 bg-white focus-visible:ring-1 focus-visible:ring-brand-ink";
  const numInput = `${inputClass} tabular-nums`;
  const locked = !!editing?.locked;
  // Lock UX: pointer-events-none disables all clicks/typing; opacity dims.
  // Submit buttons sit OUTSIDE this wrapper so we can show a "View only" notice.
  const lockClass = locked ? "pointer-events-none opacity-95" : "";

  return (
    <AppShell title="Property IC" subtitle={mode === "drafts" ? "BD Drafts & Mails" : "IC Submission"}>
      <div className="space-y-4 max-w-[1480px]">
      {mode === "drafts" ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:h-[calc(100vh-8rem)]" data-testid="ic-drafts-page">
          <PipelinePanel
            items={pipe.items} actioned={pipe.actioned}
            loading={pipe.loading} error={pipe.error}
            sync={pipe.sync}
            onRefresh={loadPipeline} onUse={onUseEmail}
            onDismiss={onDismiss} onQuickDecide={onQuickDecide}
            canApprove={canApprove}
          />
          <SubmissionsHistory subs={subs} onOpen={onOpenSub} onDelete={onDeleteSub} canApprove={canApprove} editingId={editing?.id} isAdmin={!!user?.is_admin} />
        </div>
      ) : (
        <div className="space-y-4" data-testid="ic-submission-page">
          {/* Edit / Lock banner (extracted to a child component) */}
          <ICEditBanner
            editing={editing}
            meta={meta}
            locked={locked}
            shooting={shooting}
            onScreenshot={handleScreenshot}
            onResetForm={resetForm}
          />
          <div ref={formRef} className={`${lockClass} space-y-4`} aria-disabled={locked} data-testid="ic-form-wrapper">
          {/* ==== Row 1: Property Details (left) + Room Breakdown (right) ==== */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start" data-testid="ic-row-1">
          <Section icon={Building} color="#FFCC00" title="Property details" compact>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              <Field label="Property name" span={3} compact>
                <Input className={inputClass} value={meta.property_name} onChange={e => setMeta(m => ({ ...m, property_name: e.target.value }))} data-testid="ic-property-name" />
              </Field>
              <Field label="City" compact>
                <Input className={inputClass} value={meta.city} onChange={e => setMeta(m => ({ ...m, city: e.target.value }))} data-testid="ic-city" />
              </Field>
              <Field label="State" compact>
                <Input className={inputClass} value={meta.state} onChange={e => setMeta(m => ({ ...m, state: e.target.value }))} data-testid="ic-state" />
              </Field>
              <Field label="Partner" compact>
                <Input className={inputClass} value={meta.partner_name} onChange={e => setMeta(m => ({ ...m, partner_name: e.target.value }))} data-testid="ic-partner" />
              </Field>
              <Field label="Operation start" compact>
                <Input type="date" className={inputClass} value={meta.operation_start_date} onChange={e => setMeta(m => ({ ...m, operation_start_date: e.target.value }))} data-testid="ic-start-date" />
              </Field>
              <Field label="Model" compact>
                <Select value={params.model} onValueChange={v => setParams(p => ({ ...p, model: v }))}>
                  <SelectTrigger className={inputClass} data-testid="ic-model"><SelectValue /></SelectTrigger>
                  <SelectContent>{MODELS.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Type" compact>
                <Select value={params.type} onValueChange={v => setParams(p => ({ ...p, type: v }))}>
                  <SelectTrigger className={inputClass} data-testid="ic-type"><SelectValue /></SelectTrigger>
                  <SelectContent>{TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Vintage" compact>
                <Select value={params.vintage} onValueChange={v => setParams(p => ({ ...p, vintage: v }))}>
                  <SelectTrigger className={inputClass} data-testid="ic-vintage"><SelectValue /></SelectTrigger>
                  <SelectContent>{VINTAGES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Property link" span={2} compact>
                <div className="flex gap-1.5 items-center">
                  <Input
                    className={`${inputClass} flex-1`}
                    value={meta.property_link || ""}
                    placeholder="Google Maps / MagicBricks / 99acres URL"
                    onChange={e => setMeta(m => ({ ...m, property_link: e.target.value }))}
                    data-testid="ic-property-link"
                  />
                  {meta.property_link && (
                    <a
                      href={meta.property_link}
                      target="_blank"
                      rel="noreferrer"
                      data-testid="ic-property-link-open"
                      className="h-9 px-3 rounded border border-zinc-200 bg-white text-[11.5px] font-medium text-zinc-700 hover:bg-zinc-50 inline-flex items-center gap-1 pointer-events-auto"
                      title="Open in new tab"
                    >
                      <MapPin className="w-3.5 h-3.5" /> Open
                    </a>
                  )}
                </div>
              </Field>
            </div>
            <div className="mt-2 flex flex-wrap gap-2 items-center px-3 py-1.5 bg-zinc-50/80 rounded-md border border-zinc-100">
              <span className="text-[11px] text-zinc-500">Capex auto-calc</span>
              <span className="text-zinc-300">·</span>
              <span className="text-[12px] font-medium text-zinc-700">{params.model} = ₹{params.model === "COCO" ? "80K" : "40K"}/bed</span>
              <span className="text-zinc-300">·</span>
              <span className="text-[12px] text-zinc-600">{rs.total_beds} beds × ₹{params.model === "COCO" ? "80K" : "40K"} =</span>
              <span className="text-[14px] font-display font-black text-zinc-900" data-testid="ic-capex-total">{fmtL(sc?.capex)}</span>
              <span className="text-[11px] text-zinc-400 ml-auto" data-testid="ic-capex-per-bed">₹{(sc?.capex_per_bed || 0).toLocaleString("en-IN")}/bed</span>
            </div>
            {meta.source_email_id && (meta.source_email_body || meta.source_email_subject) && (
              <SourceEmailPanel
                subject={meta.source_email_subject}
                from={meta.source_email_from}
                date={meta.source_email_date}
                body={meta.source_email_body}
              />
            )}
          </Section>

          {/* Room breakdown — Option 1/2/3 (active drives all calcs) */}
          <Section icon={Bed} color="#FF69B4" title="Room breakdown" action={
            <div className="flex items-center gap-1" data-testid="ic-rooms-tabs">
              {[0, 1, 2].map(idx => {
                const opt = roomOptions[idx] || EMPTY_ROOMS;
                const beds = (opt.dorm_4 * 4) + (opt.dorm_6 * 6) + (opt.dorm_8 * 8) + (opt.dorm_10 * 10) + (opt.dorm_12 * 12) + (opt.dorm_16 * 16) + (opt.private * 2);
                const isActive = idx === activeOption;
                return (
                  <button
                    key={idx}
                    onClick={() => setActiveOption(idx)}
                    data-testid={`ic-rooms-tab-${idx + 1}`}
                    title={`${beds} beds in this option`}
                    className={`text-[11px] font-medium px-2.5 py-1 rounded border transition-colors ${isActive ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-700 border-zinc-200 hover:border-zinc-400"}`}
                  >
                    Option {idx + 1}
                    <span className={`ml-1 text-[9.5px] tabular-nums ${isActive ? "text-zinc-300" : "text-zinc-400"}`}>· {beds}b</span>
                  </button>
                );
              })}
              <button
                onClick={() => {
                  if (window.confirm(`Clear Option ${activeOption + 1} room breakdown?`)) {
                    setRooms({ ...EMPTY_ROOMS });
                  }
                }}
                data-testid="ic-rooms-clear"
                className="text-[10px] text-zinc-500 hover:text-rose-600 px-1.5 py-1 ml-1"
                title="Clear this option"
              >
                clear
              </button>
            </div>
          }>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {DORM_SIZES.map(s => (
                <Field key={s} label={`${s}-bed dorm`}>
                  <Input type="number" min="0" className={numInput} value={rooms[`dorm_${s}`]} onChange={e => setRooms(r => ({ ...r, [`dorm_${s}`]: +e.target.value || 0 }))} {...numProps} data-testid={`ic-rooms-dorm-${s}`} />
                </Field>
              ))}
              <Field label="Private (2 beds)">
                <Input type="number" min="0" className={numInput} value={rooms.private} onChange={e => setRooms(r => ({ ...r, private: +e.target.value || 0 }))} {...numProps} data-testid="ic-rooms-private" />
              </Field>
              <Field label="Blocked beds (staff / maint.)">
                <Input type="number" min="0" className={numInput} value={rooms.blocked || 0} onChange={e => setRooms(r => ({ ...r, blocked: Math.max(0, +e.target.value || 0) }))} {...numProps} data-testid="ic-rooms-blocked" />
              </Field>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mt-4">
              <Stat label="Total rooms" value={rs.total_rooms} accent="#8FCAE7" testid="ic-total-rooms" />
              <Stat label="Dorm beds" value={rs.dorm_beds} accent="#FF69B4" testid="ic-dorm-beds" />
              <Stat label="Private beds" value={rs.private_beds} accent="#9370DB" testid="ic-private-beds" />
              <Stat label="Dorm bed:room ratio" value={dormBedRoomRatio} sub={`${rs.dorm_beds} beds / ${rs.dorm_rooms} rooms`} accent="#20B2AA" testid="ic-dorm-bed-room-ratio" />
              <div className="bg-brand-yellow border border-zinc-900/10 rounded-lg p-3" data-testid="ic-total-beds-card">
                <div className="text-[10px] font-medium uppercase tracking-wide text-zinc-900/70">Total beds</div>
                <div className="font-display font-black text-[24px] leading-tight text-zinc-900 mt-0.5" data-testid="ic-total-beds">{rs.total_beds}</div>
                <div className="text-[10px] text-zinc-700 mt-0.5">Bed-nights / mo: {fmtNum(sc?.total_bed_nights)}</div>
                {rs.blocked_beds > 0 && (
                  <div className="text-[10px] font-medium text-zinc-900 mt-0.5" data-testid="ic-sellable-beds">Sellable {rs.sellable_beds} · Blocked {rs.blocked_beds}</div>
                )}
              </div>
            </div>
          </Section>
          </div>

          {/* ==== Row 2: Estimates (left) + Compressed KPI strip (right) ==== */}
          {/* items-start so neither column is stretched to match the other. */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start" data-testid="ic-row-2">
          <Section icon={Users} color="#20B2AA" title={`Estimates · ${memberEstimates.length} BD member${memberEstimates.length === 1 ? "" : "s"}`} action={
            <div className="flex items-center gap-2">
              {calcLoading && <Loader2 className="w-3 h-3 animate-spin text-zinc-400" />}
              <Button onClick={onAiSuggest} disabled={aiLoading || !meta.city} size="sm" className="h-8 rounded-md bg-zinc-900 text-white hover:bg-zinc-800 text-[11px] gap-1.5" data-testid="ic-ai-suggest">
                {aiLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Wand2 className="w-3 h-3" />} AI suggest
              </Button>
            </div>
          }>
            {aiSugg && (
              <div className="mb-3 p-3 bg-brand-yellow/15 border border-brand-yellow/40 rounded-lg" data-testid="ic-ai-rationale">
                <div className="font-display font-bold text-[12.5px] mb-1 flex items-center gap-1"><Sparkles className="w-3 h-3" /> AI rationale</div>
                <p className="text-[11.5px] text-zinc-700 leading-relaxed">{aiSugg.summary}</p>
                <div className="text-[11px] text-zinc-600 mt-1.5"><b>Dorm:</b> {aiSugg.dorm?.rationale}</div>
                <div className="text-[11px] text-zinc-600"><b>Private:</b> {aiSugg.private?.rationale}</div>
              </div>
            )}

            {/* Prod-style vertical table — one member per row, 4 metric columns */}
            <div className="overflow-x-auto rounded-lg border border-zinc-200">
              <table className="w-full text-[12.5px]" data-testid="ic-member-estimates">
                <thead>
                  <tr className="bg-zinc-50 text-left">
                    <th className="px-3 py-2 font-medium text-[11px] text-zinc-600">BD member</th>
                    <th className="px-2 py-2 font-medium text-[11px] text-rose-700" colSpan={2}>Dorm <span className="font-normal text-rose-500">(per bed)</span></th>
                    <th className="px-2 py-2 font-medium text-[11px] text-violet-700" colSpan={2}>Private <span className="font-normal text-violet-500">(per room)</span></th>
                  </tr>
                  <tr className="bg-zinc-50/60 text-left text-zinc-500 text-[10.5px] border-t border-zinc-100">
                    <th></th>
                    <th className="px-2 py-1.5 font-medium">Occ %</th>
                    <th className="px-2 py-1.5 font-medium">ADR (₹)</th>
                    <th className="px-2 py-1.5 font-medium">Occ %</th>
                    <th className="px-2 py-1.5 font-medium">ADR / room (₹)</th>
                  </tr>
                </thead>
                <tbody>
                  {memberEstimates.map((m, idx) => (
                    <tr key={`${idx}-${m.member}`} className="border-t border-zinc-100 hover:bg-zinc-50/40">
                      <td className="px-3 py-1.5 font-display font-bold text-[12.5px] text-zinc-900">
                        {renamingIdx === idx ? (
                          <div className="flex flex-col gap-0.5">
                            <Input
                              type="text"
                              autoFocus
                              className="h-7 rounded-md border-zinc-200 text-[12px] py-0"
                              value={renameValue}
                              onChange={e => { setRenameValue(e.target.value); if (renameError?.idx === idx) setRenameError(null); }}
                              onKeyDown={e => {
                                if (e.key === "Enter") { e.preventDefault(); commitRename(idx); }
                                else if (e.key === "Escape") { e.preventDefault(); setRenamingIdx(null); setRenameError(null); }
                              }}
                              onBlur={() => commitRename(idx)}
                              data-testid={`ic-member-rename-input-${idx}`}
                            />
                            {renameError?.idx === idx && (
                              <span className="text-[10px] text-rose-600" data-testid={`ic-member-rename-error-${idx}`}>{renameError.msg}</span>
                            )}
                          </div>
                        ) : (
                          <div className="flex items-center gap-1.5">
                            <span>{m.member}</span>
                            {isAdmin && editMembersMode && (
                              <div className="flex items-center gap-0.5 ml-auto">
                                <button type="button" onClick={() => { setRenamingIdx(idx); setRenameValue(m.member); setRenameError(null); }} className="text-zinc-400 hover:text-zinc-900 p-0.5" title="Rename" data-testid={`ic-member-rename-btn-${idx}`}>
                                  <Pencil className="w-3 h-3" />
                                </button>
                                <button type="button" onClick={() => deleteMember(idx)} className="text-zinc-400 hover:text-rose-600 p-0.5" title="Delete member" data-testid={`ic-member-delete-btn-${idx}`}>
                                  <Trash2 className="w-3 h-3" />
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="px-2 py-1"><Input type="number" className="h-8 rounded-md border-zinc-200 text-[12px] tabular-nums" value={m.dorm_occ_pct} onChange={e => setMemberEstimates(arr => arr.map((x,i) => i===idx ? { ...x, dorm_occ_pct: +e.target.value || 0 } : x))} {...numProps} data-testid={`ic-member-${m.member.toLowerCase()}-dorm-occ`} /></td>
                      <td className="px-2 py-1"><Input type="number" className="h-8 rounded-md border-zinc-200 text-[12px] tabular-nums" value={m.dorm_adr} onChange={e => setMemberEstimates(arr => arr.map((x,i) => i===idx ? { ...x, dorm_adr: +e.target.value || 0 } : x))} {...numProps} data-testid={`ic-member-${m.member.toLowerCase()}-dorm-adr`} /></td>
                      <td className="px-2 py-1"><Input type="number" className="h-8 rounded-md border-zinc-200 text-[12px] tabular-nums" value={m.private_occ_pct} onChange={e => setMemberEstimates(arr => arr.map((x,i) => i===idx ? { ...x, private_occ_pct: +e.target.value || 0 } : x))} {...numProps} data-testid={`ic-member-${m.member.toLowerCase()}-private-occ`} /></td>
                      <td className="px-2 py-1"><Input type="number" className="h-8 rounded-md border-zinc-200 text-[12px] tabular-nums" value={m.private_adr} onChange={e => setMemberEstimates(arr => arr.map((x,i) => i===idx ? { ...x, private_adr: +e.target.value || 0 } : x))} {...numProps} data-testid={`ic-member-${m.member.toLowerCase()}-private-adr`} /></td>
                    </tr>
                  ))}
                  {/* Blank row shown while the admin is adding a new member. */}
                  {/* Auto-focused name input; Enter/blur commits, Escape/trash discards. */}
                  {isAdmin && addingMember && (
                    <tr className="border-t border-zinc-100 bg-emerald-50/40" data-testid="ic-member-new-row">
                      <td className="px-3 py-1.5">
                        <div className="flex flex-col gap-0.5">
                          <div className="flex items-center gap-1.5">
                            <Input
                              type="text"
                              autoFocus
                              placeholder="New member name"
                              className="h-7 rounded-md border-zinc-200 text-[12px] py-0"
                              value={newMemberName}
                              onChange={e => { setNewMemberName(e.target.value); if (addError) setAddError(null); }}
                              onKeyDown={e => {
                                if (e.key === "Enter") { e.preventDefault(); commitAdd(); }
                                else if (e.key === "Escape") { e.preventDefault(); cancelAdd(); }
                              }}
                              onBlur={() => commitAdd()}
                              data-testid="ic-member-new-input"
                            />
                            <button type="button" onMouseDown={(e) => { e.preventDefault(); document.querySelector('[data-testid="ic-member-new-input"]')?.focus(); }} className="text-zinc-400 hover:text-zinc-900 p-0.5" title="Focus name" data-testid="ic-member-new-rename-btn">
                              <Pencil className="w-3 h-3" />
                            </button>
                            <button type="button" onMouseDown={(e) => { e.preventDefault(); cancelAdd(); }} className="text-zinc-400 hover:text-rose-600 p-0.5" title="Discard" data-testid="ic-member-new-delete-btn">
                              <Trash2 className="w-3 h-3" />
                            </button>
                          </div>
                          {addError && <span className="text-[10px] text-rose-600" data-testid="ic-member-new-error">{addError}</span>}
                        </div>
                      </td>
                      <td className="px-2 py-1"><Input type="number" className="h-8 rounded-md border-zinc-200 text-[12px] tabular-nums text-zinc-400" value={0} disabled /></td>
                      <td className="px-2 py-1"><Input type="number" className="h-8 rounded-md border-zinc-200 text-[12px] tabular-nums text-zinc-400" value={0} disabled /></td>
                      <td className="px-2 py-1"><Input type="number" className="h-8 rounded-md border-zinc-200 text-[12px] tabular-nums text-zinc-400" value={0} disabled /></td>
                      <td className="px-2 py-1"><Input type="number" className="h-8 rounded-md border-zinc-200 text-[12px] tabular-nums text-zinc-400" value={0} disabled /></td>
                    </tr>
                  )}
                  <tr className="border-t-2 border-brand-yellow bg-brand-yellow/30">
                    <td className="px-3 py-2 font-display font-black text-[12.5px] text-zinc-900">AVERAGE</td>
                    <td className="px-2 py-2 font-display font-black text-[13px] tabular-nums" data-testid="ic-avg-dorm-occ">{memberEstimates.length ? fmtPct(estDorm.occ_pct) : "—"}</td>
                    <td className="px-2 py-2 font-display font-black text-[13px] tabular-nums" data-testid="ic-avg-dorm-adr">{memberEstimates.length ? `₹${Math.round(estDorm.adr).toLocaleString("en-IN")}` : "—"}</td>
                    <td className="px-2 py-2 font-display font-black text-[13px] tabular-nums" data-testid="ic-avg-private-occ">{memberEstimates.length ? fmtPct(estPriv.occ_pct) : "—"}</td>
                    <td className="px-2 py-2 font-display font-black text-[13px] tabular-nums" data-testid="ic-avg-private-adr">{memberEstimates.length ? `₹${Math.round(estPriv.adr).toLocaleString("en-IN")}` : "—"}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* F&B ADR input + helper — kept attached under the Estimates table.
             *  Flex-wrap so the F&B group sizes to intrinsic content (needed so
             *  the nowrap helper text stays on a single line); admin buttons
             *  pushed to the right edge via `ml-auto`. On viewports too narrow
             *  to fit both on one line, buttons wrap below and stay right-aligned. */}
            <div className="mt-4 flex flex-wrap items-start gap-3">
              <Field label="F&B ADR per occupied bed / day (₹)">
                <Input type="number" className={numInput} value={params.fnb_per_bed_per_day} onChange={e => setParams(p => ({ ...p, fnb_per_bed_per_day: +e.target.value || 0 }))} {...numProps} data-testid="ic-fnb" />
                <div className="text-[10px] text-zinc-400 mt-1 whitespace-nowrap">Multiplied by sold bed-nights → occupancy-weighted across dorm + private</div>
              </Field>
              {isAdmin && (
                <div className="flex items-center gap-2 ml-auto flex-shrink-0" data-testid="ic-members-controls">
                  <button
                    type="button"
                    onClick={() => { setEditMembersMode(v => !v); setRenamingIdx(null); setRenameError(null); }}
                    className={`h-9 px-3 rounded-md border text-[12px] font-medium inline-flex items-center gap-1.5 transition-colors ${
                      editMembersMode
                        ? "border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                        : "border-zinc-200 bg-white text-zinc-700 hover:bg-zinc-50"
                    }`}
                    data-testid="ic-members-edit-toggle"
                  >
                    {editMembersMode ? <Check className="w-3.5 h-3.5" /> : <Pencil className="w-3.5 h-3.5" />}
                    {editMembersMode ? "Done" : "Edit members"}
                  </button>
                  <button
                    type="button"
                    onClick={() => { setAddingMember(true); setNewMemberName(""); setAddError(null); }}
                    className="h-9 px-3 rounded-md border border-zinc-200 bg-white text-zinc-700 text-[12px] font-medium inline-flex items-center gap-1.5 hover:bg-zinc-50 transition-colors"
                    data-testid="ic-members-add-btn"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    Add member
                  </button>
                </div>
              )}
            </div>
          </Section>
          <div className="flex flex-col gap-3" data-testid="ic-row-2-right">
            <KpiStrip sc={sc} />
            <RevenueBand sc={sc} rs={rs} params={params} />
          </div>
          </div>

          {/* ==== Row 3: Direct & Operational Cost table (DOC only, full-width) ==== */}
          <div data-testid="ic-row-3">
            <OpexTable sc={sc} opOverrides={opOverrides} setOpOverrides={setOpOverrides} misRates={misRates} params={params} />
          </div>

          {/* ==== Row 4a: Financial totals strip + Decision metrics strip + Scenario toggle ==== */}
          <div data-testid="ic-row-4-metrics" className="space-y-2">
            <FinancialTotalsStrip sc={sc} rs={rs} misRates={misRates} />
            <DecisionMetricsStrip sc={sc} rs={rs} breakeven={breakeven} model={params.model} />
            <ScenarioToggle activeScenario={activeScenario} setActiveScenario={setActiveScenario} />
          </div>
          </div>
          {/* ==== Row 4b: Save/Send bar (full-width, outside lock wrapper) ==== */}
          <ICSubmitBar
            locked={locked}
            editing={editing}
            canApprove={canApprove}
            submitting={submitting}
            shooting={shooting}
            onSubmit={submit}
            onScreenshot={handleScreenshot}
            onResetForm={resetForm}
          />

          {/* ==== Row 5: MIS Intelligence (left) + Market & Competitor Intelligence (right) ==== */}
          <div className={`${lockClass} grid grid-cols-1 lg:grid-cols-2 gap-4 items-start`} aria-disabled={locked} data-testid="ic-row-5">
            <MisIntelligence allProperties={misProps} currentCity={meta.city} onApply={onApplyMis} />
            <MarketCompetitorIntel city={meta.city} state={meta.state} propertyName={meta.property_name} />
          </div>
        </div>
      )}
      </div>

      {/* Off-screen one-pager IC document for the Screenshot button. */}
      {/* Rendered always (so html2canvas can capture it) but positioned off-screen. */}
      <div
        aria-hidden="true"
        style={{
          position: "fixed",
          left: -10000,
          top: 0,
          width: 820,
          pointerEvents: "none",
          zIndex: -1,
        }}
      >
        <div ref={docRef} data-testid="ic-document-snapshot">
          <ICDocument
            meta={meta}
            params={params}
            rooms={rooms}
            estDorm={estDorm}
            estPriv={estPriv}
            calc={calc}
            breakeven={breakeven}
            user={user}
            generatedAt={new Date().toISOString()}
            decision={editing?.decision}
          />
        </div>
      </div>
    </AppShell>
  );
}
